import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, FormControl, InputLabel, Select, MenuItem,
  Typography, useTheme, LinearProgress,
} from '@mui/material';
import { ArrowDownward, AttachMoney } from '@mui/icons-material';
import {
  GlassCard, StatCard, ShimmerSkeleton, EmptyState,
} from '../../../components/admin/ui';
import { getConversions } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

// ─── Funnel options ────────────────────────────────────────────────────────────

const FUNNEL_OPTIONS = [
  { value: 'register_to_first_booking', label: 'Register → First Booking' },
  { value: 'library_view_to_booking', label: 'Library View → Booking' },
  { value: 'institute_lead_to_contact', label: 'Institute Lead → Contact' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(seconds) {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsConversions({ filters }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [funnel, setFunnel] = useState(FUNNEL_OPTIONS[0].value);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getConversions({
      from: filters.from,
      to: filters.to,
      funnel,
    }).then((d) => {
      if (!cancelled) { setData(d); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, funnel]);

  const steps = data?.funnel?.steps ?? [];
  const maxUsers = steps.reduce((acc, s) => Math.max(acc, s.users || 0), 0);
  const revenuePaise = data?.revenuePaise ?? 0;

  return (
    <Box>
      {/* ── Funnel selector + revenue ───────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Funnel</InputLabel>
          <Select
            value={funnel}
            onChange={(e) => setFunnel(e.target.value)}
            label="Funnel"
          >
            {FUNNEL_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <StatCard
          label="Revenue (period)"
          value={Math.round(revenuePaise / 100)}
          prefix="₹"
          icon={<AttachMoney />}
          iconIndex={2}
          loading={loading}
          index={0}
        />
      </Box>

      {/* ── Funnel steps ────────────────────────────────────────────────── */}
      {loading ? (
        <ShimmerSkeleton height={400} />
      ) : steps.length === 0 ? (
        <EmptyState
          title="No funnel data"
          description="Funnel data will appear once the analytics backend is live."
          iconIndex={1}
        />
      ) : (
        <Box sx={{ position: 'relative' }}>
          {steps.map((step, i) => {
            const barPct = maxUsers > 0 ? (step.users / maxUsers) * 100 : 0;
            const isLast = i === steps.length - 1;

            return (
              <GlassCard key={i} sx={{ p: 3, mb: 2, position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontSize: '0.68rem',
                        color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)',
                      }}
                    >
                      Step {i + 1}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}
                    >
                      {step.name}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}>
                      {(step.users || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                      users
                    </Typography>
                  </Box>
                </Box>

                {/* Progress bar */}
                <LinearProgress
                  variant="determinate"
                  value={barPct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
                    '& .MuiLinearProgress-bar': {
                      background: `linear-gradient(90deg, ${ADMIN_COLORS.accentIndigo}, ${ADMIN_COLORS.accentCyan})`,
                      borderRadius: 4,
                    },
                  }}
                />

                {/* Drop-off + time to next */}
                {!isLast && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <ArrowDownward sx={{ fontSize: 14, color: ADMIN_COLORS.danger }} />
                    <Typography
                      variant="caption"
                      sx={{ color: ADMIN_COLORS.danger, fontWeight: 600 }}
                    >
                      {step.dropOffPct != null ? `${(step.dropOffPct * 100).toFixed(1)}% drop-off` : '—'}
                    </Typography>
                    {step.avgTimeToNextSec != null && (
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}
                      >
                        · avg {formatTime(step.avgTimeToNextSec)} to next step
                      </Typography>
                    )}
                  </Box>
                )}
              </GlassCard>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

AnalyticsConversions.propTypes = {
  filters: PropTypes.object.isRequired,
};
